import React from "react";
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBCardText,
  MDBIcon,
  MDBBadge,
  MDBSpinner,
  MDBBtn,
  MDBBtnGroup,
} from "mdb-react-ui-kit";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import "./CCONMainComponent.css";
import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { BalanceByAccountCard } from "../BalanceByAccountCard/BalanceByAccountCard";
import { SavingsRatioCard } from "../SavingsRatioCard/SavingsRatioCard";
import { InterannualComparisonChart } from "../InterannualComparisonChart/InterannualComparisonChart";
import { CategoryBreakdownTabs } from "../CategoryBreakdownTabs/CategoryBreakdownTabs";
import { ToggleablePieCard } from "../ToggleablePieCard/ToggleablePieCard";
import { GastoPeriodicoModal } from "../GastoPeriodicoModal/GastoPeriodicoModal";
import { ConfirmarOcurrenciaModal } from "../ConfirmarOcurrenciaModal/ConfirmarOcurrenciaModal";

const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const PIE_COLORS = ["#1266f1", "#00bfa5", "#ff7043", "#ab47bc", "#ffa000", "#26c6da", "#8d6e63", "#ef5350"];

export default class CCONMainComponent extends React.Component {
  constructor(props) {
    super(props);

    const hoy = new Date();
    const periodoStart = new Date(hoy.getFullYear(), hoy.getMonth() - 11, 1);

    this.state = {
      loading: true,
      refreshing: false,
      data: null,
      error: false,
      modoVista: "contable",
      selectedMonth: hoy.getMonth() + 1,
      selectedYear: hoy.getFullYear(),
      periodUnit: "M",
      periodValue: 12,
      useManualPeriodRange: false,
      periodStartMonth: periodoStart.getMonth() + 1,
      periodStartYear: periodoStart.getFullYear(),
      periodEndMonth: hoy.getMonth() + 1,
      periodEndYear: hoy.getFullYear(),
      // Gastos periódicos (Fase 7)
      gastosPeriodicos: [],
      ocurrenciasPendientes: [],
      categorias: [],
      userAccounts: [],
      loadingPeriodicos: false,
      showGastoPeriodicoModal: false,
      gastoToEdit: null,
      showConfirmarOcurrenciaModal: false,
      ocurrenciaSeleccionada: null,
    };

    this.toggleModoVista = this.toggleModoVista.bind(this);
    this.adjustSelectedMonth = this.adjustSelectedMonth.bind(this);
    this.setSelectedMonth = this.setSelectedMonth.bind(this);
    this.setSelectedYear = this.setSelectedYear.bind(this);
    this.setPeriodUnit = this.setPeriodUnit.bind(this);
    this.setPeriodValue = this.setPeriodValue.bind(this);
    this.setPeriodStartMonth = this.setPeriodStartMonth.bind(this);
    this.setPeriodStartYear = this.setPeriodStartYear.bind(this);
    this.setPeriodEndMonth = this.setPeriodEndMonth.bind(this);
    this.setPeriodEndYear = this.setPeriodEndYear.bind(this);
    this.adjustPeriodStartMonth = this.adjustPeriodStartMonth.bind(this);
    this.adjustPeriodEndMonth = this.adjustPeriodEndMonth.bind(this);
    this.activateManualRange = this.activateManualRange.bind(this);
    this.useNumericPeriod = this.useNumericPeriod.bind(this);
    this.switchPeriodMode = this.switchPeriodMode.bind(this);
    this.loadGastosPeriodicos = this.loadGastosPeriodicos.bind(this);
    this.onGastoSaved = this.onGastoSaved.bind(this);
    this.onOcurrenciaConfirmada = this.onOcurrenciaConfirmada.bind(this);
    this.onOcurrenciaRechazada = this.onOcurrenciaRechazada.bind(this);
    this.finalizarGastoPeriodico = this.finalizarGastoPeriodico.bind(this);
    this.deleteGastoPeriodico = this.deleteGastoPeriodico.bind(this);
  }

  componentDidMount() {
    this.loadDashboard();
    this.loadGastosPeriodicos();
  }

  loadDashboard() {
    const {
      modoVista,
      selectedMonth,
      selectedYear,
      periodUnit,
      periodValue,
      useManualPeriodRange,
      periodStartMonth,
      periodStartYear,
      periodEndMonth,
      periodEndYear,
    } = this.state;

    const params = new URLSearchParams();
    params.set("modoVista", modoVista);
    params.set("selectedMonth", String(selectedMonth));
    params.set("selectedYear", String(selectedYear));
    params.set("periodUnit", periodUnit);
    params.set("periodValue", String(Math.max(1, Number(periodValue) || 1)));

    if (useManualPeriodRange) {
      params.set("periodStartMonth", String(periodStartMonth));
      params.set("periodStartYear", String(periodStartYear));
      params.set("periodEndMonth", String(periodEndMonth));
      params.set("periodEndYear", String(periodEndYear));
    }

    const hasData = !!this.state.data;
    this.setState({
      loading: !hasData,
      refreshing: hasData,
      error: false,
    });

    doFetch("CCON", `getDashboardData?${params.toString()}`, null)
      .then((r) => r.json())
      .then((res) => {
        if (res.STATE === "SUCCESS") {
          this.setState({ data: res.DATA, loading: false, refreshing: false });
        } else {
          this.setState({ error: true, loading: false, refreshing: false });
        }
      })
      .catch(() => this.setState({ error: true, loading: false, refreshing: false }));
  }

  // ── Gastos periódicos (Fase 7) ──────────────────────────────────────

  async loadGastosPeriodicos() {
    this.setState({ loadingPeriodicos: true });
    try {
      // 1. Disparar motor idempotente de generación de ocurrencias
      await doFetch("CCON", "generarOcurrencias", { method: "POST" });

      // 2. Cargar gastos configurados
      const [resGastos, resOcurrencias, resCategorias, resAccounts] = await Promise.all([
        doFetch("CCON", "getGastosPeriodicos", null).then((r) => r.json()),
        doFetch("CCON", "getOcurrenciasPendientes", null).then((r) => r.json()),
        doFetch("CCON", "getAllCategorias", null).then((r) => r.json()),
        doFetch("CCON", "getUserAccounts", null).then((r) => r.json()),
      ]);

      const userAccountsMap = new Map();
      if (resAccounts.STATE === "SUCCESS" && Array.isArray(resAccounts.DATA)) {
        for (const row of resAccounts.DATA) {
          const cuenta = row.IDCuentaFK;
          if (cuenta && !userAccountsMap.has(cuenta.IDCuenta)) {
            userAccountsMap.set(cuenta.IDCuenta, cuenta);
          }
        }
      }

      this.setState({
        gastosPeriodicos:    resGastos.STATE     === "SUCCESS" ? (resGastos.DATA     || []) : [],
        ocurrenciasPendientes: resOcurrencias.STATE === "SUCCESS" ? (resOcurrencias.DATA || []) : [],
        categorias:          resCategorias.STATE === "SUCCESS" ? (resCategorias.DATA || []) : [],
        userAccounts: Array.from(userAccountsMap.values()),
        loadingPeriodicos: false,
      });
    } catch {
      this.setState({ loadingPeriodicos: false });
    }
  }

  onGastoSaved() {
    this.setState({ showGastoPeriodicoModal: false, gastoToEdit: null });
    this.loadGastosPeriodicos();
  }

  onOcurrenciaConfirmada() {
    this.setState({ showConfirmarOcurrenciaModal: false, ocurrenciaSeleccionada: null });
    this.loadGastosPeriodicos();
  }

  onOcurrenciaRechazada() {
    this.setState({ showConfirmarOcurrenciaModal: false, ocurrenciaSeleccionada: null });
    this.loadGastosPeriodicos();
  }

  async finalizarGastoPeriodico(gasto) {
    const idGasto = gasto?.IDGastoPeriodico || gasto?.iDGastoPeriodico;
    if (!idGasto) return;
    if (!window.confirm("¿Finalizar este gasto periódico?")) return;

    this.setState({ loadingPeriodicos: true });
    try {
      await doFetch("CCON", "finalizarGastoPeriodico", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idGastoPeriodico: idGasto }),
      });
      this.loadGastosPeriodicos();
    } catch {
      this.setState({ loadingPeriodicos: false });
    }
  }

  async deleteGastoPeriodico(gasto) {
    const idGasto = gasto?.IDGastoPeriodico || gasto?.iDGastoPeriodico;
    if (!idGasto) return;
    if (!window.confirm("¿Eliminar este gasto periódico?")) return;

    this.setState({ loadingPeriodicos: true });
    try {
      await doFetch("CCON", "deleteGastoPeriodico", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idGastoPeriodico: idGasto }),
      });
      this.loadGastosPeriodicos();
    } catch {
      this.setState({ loadingPeriodicos: false });
    }
  }

  // ───────────────────────────────────────────────────────────────────

  toggleModoVista() {
    this.setState(
      (state) => ({
        modoVista: state.modoVista === "bancaria" ? "contable" : "bancaria",
      }),
      this.loadDashboard
    );
  }

  formatCurrency(value) {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    }).format(value ?? 0);
  }

  formatPercent(current, previous) {
    if (!previous || previous === 0) return null;
    const diff = ((current - previous) / Math.abs(previous)) * 100;
    return diff.toFixed(1);
  }

  formatMonthYear(month, year) {
    const safeMonth = Math.max(1, Math.min(12, Number(month) || 1));
    return `${MONTH_NAMES[safeMonth - 1]} ${year}`;
  }

  adjustSelectedMonth(delta) {
    this.setState(
      (prevState) => {
        let month = Number(prevState.selectedMonth) + delta;
        let year = Number(prevState.selectedYear);

        if (month > 12) {
          month = 1;
          year += 1;
        } else if (month < 1) {
          month = 12;
          year -= 1;
        }

        return {
          selectedMonth: month,
          selectedYear: year,
          periodEndMonth: prevState.useManualPeriodRange ? prevState.periodEndMonth : month,
          periodEndYear: prevState.useManualPeriodRange ? prevState.periodEndYear : year,
        };
      },
      this.loadDashboard
    );
  }

  setSelectedMonth(monthValue) {
    const month = Math.max(1, Math.min(12, Number(monthValue) || 1));
    this.setState(
      (prevState) => ({
        selectedMonth: month,
        periodEndMonth: prevState.useManualPeriodRange ? prevState.periodEndMonth : month,
      }),
      this.loadDashboard
    );
  }

  setSelectedYear(yearValue) {
    const year = Number(yearValue) || new Date().getFullYear();
    this.setState(
      (prevState) => ({
        selectedYear: year,
        periodEndYear: prevState.useManualPeriodRange ? prevState.periodEndYear : year,
      }),
      this.loadDashboard
    );
  }

  setPeriodUnit(unit) {
    const normalized = unit === "Y" ? "Y" : "M";
    this.setState({ periodUnit: normalized }, this.loadDashboard);
  }

  setPeriodValue(value) {
    const numeric = Math.max(1, Number(value) || 1);
    this.setState({ periodValue: numeric }, this.loadDashboard);
  }

  setPeriodStartMonth(value) {
    this.setState({ periodStartMonth: Number(value), useManualPeriodRange: true }, this.loadDashboard);
  }

  setPeriodStartYear(value) {
    this.setState({ periodStartYear: Number(value), useManualPeriodRange: true }, this.loadDashboard);
  }

  setPeriodEndMonth(value) {
    this.setState({ periodEndMonth: Number(value), useManualPeriodRange: true }, this.loadDashboard);
  }

  setPeriodEndYear(value) {
    this.setState({ periodEndYear: Number(value), useManualPeriodRange: true }, this.loadDashboard);
  }

  adjustPeriodStartMonth(delta) {
    this.setState(
      (prevState) => {
        let month = Number(prevState.periodStartMonth) + delta;
        let year = Number(prevState.periodStartYear);

        if (month > 12) {
          month = 1;
          year += 1;
        } else if (month < 1) {
          month = 12;
          year -= 1;
        }

        return {
          periodStartMonth: month,
          periodStartYear: year,
          useManualPeriodRange: true,
        };
      },
      this.loadDashboard
    );
  }

  adjustPeriodEndMonth(delta) {
    this.setState(
      (prevState) => {
        let month = Number(prevState.periodEndMonth) + delta;
        let year = Number(prevState.periodEndYear);

        if (month > 12) {
          month = 1;
          year += 1;
        } else if (month < 1) {
          month = 12;
          year -= 1;
        }

        return {
          periodEndMonth: month,
          periodEndYear: year,
          useManualPeriodRange: true,
        };
      },
      this.loadDashboard
    );
  }

  activateManualRange() {
    this.setState({ useManualPeriodRange: true }, this.loadDashboard);
  }

  useNumericPeriod() {
    this.setState({ useManualPeriodRange: false }, this.loadDashboard);
  }

  switchPeriodMode(mode) {
    const manual = mode === "manual";
    this.setState({ useManualPeriodRange: manual }, this.loadDashboard);
  }

  getEffectivePeriodLabel(data) {
    const startMonth = data?.periodStartMonth ?? this.state.periodStartMonth;
    const startYear = data?.periodStartYear ?? this.state.periodStartYear;
    const endMonth = data?.periodEndMonth ?? this.state.periodEndMonth;
    const endYear = data?.periodEndYear ?? this.state.periodEndYear;
    const months =
      (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
    return `${this.formatMonthYear(startMonth, startYear)} \u2192 ${this.formatMonthYear(endMonth, endYear)} (${months} ${months === 1 ? "mes" : "meses"})`;
  }

  renderKpiCard(icon, label, value, color, prevValue, invertTrend = false) {
    const pct = this.formatPercent(value, prevValue);
    const up = pct !== null && parseFloat(pct) > 0;
    const good = invertTrend ? !up : up;

    return (
      <MDBCard className="dashboard-kpi-card h-100">
        <MDBCardBody>
          <div className="dashboard-kpi-icon" style={{ color }}>
            <MDBIcon fas icon={icon} />
          </div>
          <MDBCardText className="dashboard-kpi-label">{label}</MDBCardText>
          <MDBCardTitle className="dashboard-kpi-value">{this.formatCurrency(value)}</MDBCardTitle>
          {pct !== null && (
            <span className={`dashboard-kpi-trend ${good ? "trend-up" : "trend-down"}`}>
              <MDBIcon fas icon={up ? "arrow-up" : "arrow-down"} className="me-1" />
              {Math.abs(pct)}% vs mes anterior
            </span>
          )}
        </MDBCardBody>
      </MDBCard>
    );
  }

  renderExpensePieCard(title, data, emptyText) {
    const pieData = Array.isArray(data) ? data.filter((x) => Number(x.total) > 0) : [];

    return (
      <MDBCard className="h-100">
        <MDBCardBody>
          <MDBCardTitle className="dashboard-section-title">
            <MDBIcon fas icon="chart-pie" className="me-2" />
            {title}
          </MDBCardTitle>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="total"
                  nameKey="categoria"
                  cx="50%"
                  cy="50%"
                  outerRadius={84}
                  labelLine={false}
                  label={({ percent }) => `${Math.round((percent || 0) * 100)}%`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`${entry.categoria}-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => this.formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-muted text-center py-3 mb-0">{emptyText}</p>
          )}
        </MDBCardBody>
      </MDBCard>
    );
  }

  renderMonthYearNavigator() {
    const currentYear = Number(this.state.selectedYear) || new Date().getFullYear();
    const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

    return (
      <div className="date-navigator dashboard-date-navigator">
        <button className="btn btn-secondary btn-sm date-nav-arrow" onClick={() => this.adjustSelectedMonth(-1)} title="Mes anterior">
          <MDBIcon fas icon="angle-left" />
        </button>

        <div className="date-stepper">
          <button className="step-btn" onClick={() => this.setSelectedYear(this.state.selectedYear + 1)} title="Año siguiente">▲</button>
          <select className="date-select" value={this.state.selectedYear} onChange={(e) => this.setSelectedYear(e.target.value)}>
            {years.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <button className="step-btn" onClick={() => this.setSelectedYear(this.state.selectedYear - 1)} title="Año anterior">▼</button>
        </div>

        <div className="date-stepper">
          <button className="step-btn" onClick={() => this.adjustSelectedMonth(1)} title="Mes siguiente">▲</button>
          <select className="date-select" value={this.state.selectedMonth} onChange={(e) => this.setSelectedMonth(e.target.value)}>
            {MONTH_NAMES.map((month, index) => (
              <option key={month} value={index + 1}>{month}</option>
            ))}
          </select>
          <button className="step-btn" onClick={() => this.adjustSelectedMonth(-1)} title="Mes anterior">▼</button>
        </div>

        <button className="btn btn-secondary btn-sm date-nav-arrow" onClick={() => this.adjustSelectedMonth(1)} title="Mes siguiente">
          <MDBIcon fas icon="angle-right" />
        </button>
      </div>
    );
  }

  renderPeriodDateNavigator(
    label,
    month,
    year,
    onAdjustMonth,
    onSetMonth,
    onSetYear,
    disabled = false,
    numericMonth = false,
    showLabel = true,
    compact = false
  ) {
    const currentYear = Number(year) || new Date().getFullYear();
    const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

    return (
      <div className="dashboard-period-date-nav-block">
        {showLabel && <label className="dashboard-period-label mb-1">{label}</label>}
        <div className={`date-navigator dashboard-date-navigator dashboard-period-compact-nav${compact ? " is-compact" : ""}${disabled ? " dashboard-period-disabled" : ""}`}>
          <button
            className="btn btn-secondary btn-sm date-nav-arrow"
            onClick={() => onAdjustMonth(-1)}
            title="Mes anterior"
            disabled={disabled}
          >
            <MDBIcon fas icon="angle-left" />
          </button>

          <div className="date-stepper">
            <button className="step-btn" onClick={() => onSetYear(Number(year) + 1)} title="Año siguiente" disabled={disabled}>▲</button>
            <select className="date-select" value={year} onChange={(e) => onSetYear(e.target.value)} disabled={disabled}>
              {years.map((y) => (
                <option key={`${label}-y-${y}`} value={y}>{y}</option>
              ))}
            </select>
            <button className="step-btn" onClick={() => onSetYear(Number(year) - 1)} title="Año anterior" disabled={disabled}>▼</button>
          </div>

          <div className="date-stepper">
            <button className="step-btn" onClick={() => onAdjustMonth(1)} title="Mes siguiente" disabled={disabled}>▲</button>
            <select className="date-select" value={month} onChange={(e) => onSetMonth(e.target.value)} disabled={disabled}>
              {MONTH_NAMES.map((m, index) => (
                <option key={`${label}-m-${m}`} value={index + 1}>
                  {numericMonth ? String(index + 1).padStart(2, "0") : m}
                </option>
              ))}
            </select>
            <button className="step-btn" onClick={() => onAdjustMonth(-1)} title="Mes anterior" disabled={disabled}>▼</button>
          </div>

          <button
            className="btn btn-secondary btn-sm date-nav-arrow"
            onClick={() => onAdjustMonth(1)}
            title="Mes siguiente"
            disabled={disabled}
          >
            <MDBIcon fas icon="angle-right" />
          </button>
        </div>
      </div>
    );
  }

  renderPeriodSelector() {
    const { useManualPeriodRange, periodUnit, periodValue,
      periodStartMonth, periodStartYear, periodEndMonth, periodEndYear } = this.state;
    const isManual = useManualPeriodRange;

    // Texto informativo del rango rápido calculado
    const quickEndMonth = periodEndMonth;
    const quickEndYear = periodEndYear;
    let quickStartMonth, quickStartYear;
    if (periodUnit === "Y") {
      const d = new Date(quickEndYear, quickEndMonth - 1, 1);
      d.setFullYear(d.getFullYear() - (Number(periodValue) - 1));
      quickStartMonth = d.getMonth() + 1;
      quickStartYear = d.getFullYear();
    } else {
      const d = new Date(quickEndYear, quickEndMonth - 1, 1);
      d.setMonth(d.getMonth() - (Number(periodValue) - 1));
      quickStartMonth = d.getMonth() + 1;
      quickStartYear = d.getFullYear();
    }

    return (
      <div className="dashboard-period-panel">
        {/* Selector de modo */}
        <div className="mb-2">
          <MDBBtnGroup size="sm">
            <MDBBtn
              color={!isManual ? "primary" : "light"}
              active={!isManual}
              onClick={() => this.switchPeriodMode("quick")}
            >
              <MDBIcon fas icon="clock" className="me-1" />
              Período rápido
            </MDBBtn>
            <MDBBtn
              color={isManual ? "primary" : "light"}
              active={isManual}
              onClick={() => this.switchPeriodMode("manual")}
            >
              <MDBIcon fas icon="sliders-h" className="me-1" />
              Rango manual
            </MDBBtn>
          </MDBBtnGroup>
        </div>

        {/* Modo rápido */}
        <div className={`dashboard-period-row${isManual ? " dashboard-period-disabled" : ""}`}>
          <div className="dashboard-period-inline">
            <input
              type="number"
              min="1"
              className="form-control form-control-sm dashboard-number-input"
              value={periodValue}
              disabled={isManual}
              onChange={(e) => this.setPeriodValue(e.target.value)}
            />
            <select
              className="form-select form-select-sm dashboard-unit-select"
              value={periodUnit}
              disabled={isManual}
              onChange={(e) => this.setPeriodUnit(e.target.value)}
            >
              <option value="M">Meses</option>
              <option value="Y">Años</option>
            </select>
          </div>
          {!isManual && (
            <div className="dashboard-period-chip mt-1">
              <MDBIcon fas icon="info-circle" className="me-1 text-muted" />
              <small className="text-muted">
                {this.formatMonthYear(quickStartMonth, quickStartYear)} — {this.formatMonthYear(quickEndMonth, quickEndYear)}
              </small>
            </div>
          )}
        </div>

        {/* Modo manual */}
        <div className={`dashboard-period-row mt-2${!isManual ? " dashboard-period-disabled" : ""}`}>
          <div className="dashboard-period-table">
            <div className="dashboard-period-table-header">Fecha inicio de rango</div>
            <div className="dashboard-period-table-header">Fecha fin de rango</div>
            <div className="dashboard-period-table-cell">
              {this.renderPeriodDateNavigator(
                "Inicio",
                periodStartMonth,
                periodStartYear,
                this.adjustPeriodStartMonth,
                this.setPeriodStartMonth,
                this.setPeriodStartYear,
                !isManual,
                true,
                false,
                true
              )}
            </div>
            <div className="dashboard-period-table-cell">
              {this.renderPeriodDateNavigator(
                "Fin",
                periodEndMonth,
                periodEndYear,
                this.adjustPeriodEndMonth,
                this.setPeriodEndMonth,
                this.setPeriodEndYear,
                !isManual,
                true,
                false,
                true
              )}
            </div>
          </div>
        </div>

        {/* Resumen textual persistente */}
        <div className="dashboard-period-summary mt-2">
          <MDBIcon fas icon="calendar-check" className="me-1" />
          <small>
            <strong>Analizando:</strong>{" "}
            {this.getEffectivePeriodLabel(this.state.data)}
          </small>
        </div>
      </div>
    );
  }

  render() {
    const { loading, refreshing, error, data, modoVista,
            ocurrenciasPendientes, gastosPeriodicos, categorias, loadingPeriodicos,
            showGastoPeriodicoModal, gastoToEdit,
            showConfirmarOcurrenciaModal, ocurrenciaSeleccionada } = this.state;
    const viewLabel = modoVista === "contable" ? "contable" : "bancaria";

    if (loading) {
      return (
        <MDBContainer fluid className="dashboard-loading">
          <MDBSpinner grow color="primary" />
        </MDBContainer>
      );
    }

    if (error || !data) {
      return (
        <MDBContainer fluid className="dashboard-loading text-muted">
          <MDBIcon fas icon="exclamation-triangle" className="me-2" />
          No se pudo cargar el dashboard.
          <button className="btn btn-link" onClick={() => this.loadDashboard()}>
            Reintentar
          </button>
        </MDBContainer>
      );
    }

    const {
      balanceTotal,
      numCuentas,
      ingresosMesActual,
      gastosMesActual,
      ingresosMesAnterior,
      gastosMesAnterior,
      topCategorias,
      evolucionMensual,
      cuentasCompartidas,
      personalAccounts,
      savingsRatio,
      interannualComparison,
      expensesTop,
      incomesTop,
      gastoCategoriasMes,
      gastoCategoriasPeriodo,
      periodExpensesTop,
      periodIncomesTop,
      selectedMonth,
      selectedYear,
      periodStartMonth,
      periodStartYear,
      periodEndMonth,
      periodEndYear,
    } = data;

    const cuentasDisponibles = (this.state.userAccounts || []).length > 0
      ? this.state.userAccounts
      : (personalAccounts || []);

    const monthLabel = this.formatMonthYear(selectedMonth || this.state.selectedMonth, selectedYear || this.state.selectedYear);
    const periodLabel = `${this.formatMonthYear(periodStartMonth || this.state.periodStartMonth, periodStartYear || this.state.periodStartYear)} - ${this.formatMonthYear(periodEndMonth || this.state.periodEndMonth, periodEndYear || this.state.periodEndYear)}`;

    return (
      <>
      <MDBContainer fluid className="dashboard-container">
        <MDBRow className="mb-3 align-items-center">
          <MDBCol md="8" className="mb-2 mb-md-0">
            <MDBBadge color={modoVista === "contable" ? "info" : "secondary"} pill>
              Dashboard en vista {viewLabel}
            </MDBBadge>
            {refreshing && (
              <MDBBadge color="warning" pill className="ms-2">
                Actualizando datos...
              </MDBBadge>
            )}
            <MDBBtn
              color={modoVista === "contable" ? "info" : "secondary"}
              outline={modoVista !== "contable"}
              onClick={this.toggleModoVista}
              title="Alterna entre fecha bancaria y fecha de imputación contable"
              size="sm"
              className="ms-2"
            >
              <MDBIcon icon="exchange-alt" className="me-1" />
              {modoVista === "contable" ? "Vista contable" : "Vista bancaria"}
            </MDBBtn>
          </MDBCol>
          <MDBCol md="4" className="text-md-end">
            <MDBBadge color="light" className="text-dark me-2" pill>
              Mes: {monthLabel}
            </MDBBadge>
            <MDBBadge color="light" className="text-dark" pill>
              Periodo: {periodLabel}
            </MDBBadge>
          </MDBCol>
        </MDBRow>

        <MDBRow className="mb-4 g-3">
          <MDBCol md="6" lg="3">{this.renderKpiCard("wallet", "Balance total", balanceTotal, "#1266f1", null)}</MDBCol>
          <MDBCol md="6" lg="3">
            <MDBCard className="dashboard-kpi-card h-100">
              <MDBCardBody>
                <div className="dashboard-kpi-icon" style={{ color: "#7b1fa2" }}>
                  <MDBIcon fas icon="credit-card" />
                </div>
                <MDBCardText className="dashboard-kpi-label">Cuentas activas</MDBCardText>
                <MDBCardTitle className="dashboard-kpi-value">{numCuentas}</MDBCardTitle>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
          <MDBCol md="12" lg="6">
            <BalanceByAccountCard personalAccounts={personalAccounts} />
          </MDBCol>
        </MDBRow>

        <MDBRow className="g-4">
          <MDBCol xl="6">
            <MDBCard className="h-100">
              <MDBCardBody>
                <MDBCardTitle className="dashboard-section-title d-flex justify-content-between align-items-center">
                  <span>
                    <MDBIcon fas icon="calendar-alt" className="me-2" />
                    Resumen mes seleccionado
                  </span>
                  <MDBBadge color="light" className="text-dark" pill>{monthLabel}</MDBBadge>
                </MDBCardTitle>

                {this.renderMonthYearNavigator()}

                <MDBRow className="g-3 mb-3">
                  <MDBCol md="6">{this.renderKpiCard("arrow-up", `Ingresos del mes ${viewLabel}`, ingresosMesActual, "#00796b", ingresosMesAnterior)}</MDBCol>
                  <MDBCol md="6">{this.renderKpiCard("arrow-down", `Gastos del mes ${viewLabel}`, gastosMesActual, "#c62828", gastosMesAnterior, true)}</MDBCol>
                  <MDBCol md="6"><SavingsRatioCard savingsRatio={savingsRatio} /></MDBCol>
                  <MDBCol md="6"><InterannualComparisonChart interannualComparison={interannualComparison} /></MDBCol>
                </MDBRow>

                <MDBRow className="g-3 mb-3">
                  <MDBCol md="6">
                    <CategoryBreakdownTabs expensesTop={expensesTop} incomesTop={incomesTop} />
                  </MDBCol>
                  <MDBCol md="6">
                    <ToggleablePieCard
                      title="Reparto de gasto del mes"
                      data={gastoCategoriasMes}
                      emptyText="Sin gastos para el mes seleccionado."
                    />
                  </MDBCol>
                </MDBRow>

                <MDBCard>
                  <MDBCardBody>
                    <MDBCardTitle className="dashboard-section-title">
                      <MDBIcon fas icon="tags" className="me-2" />
                      Top categorías de gasto del mes
                    </MDBCardTitle>
                    {topCategorias && topCategorias.length > 0 ? (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart layout="vertical" data={topCategorias} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
                          <YAxis type="category" dataKey="categoria" tick={{ fontSize: 11 }} width={100} />
                          <Tooltip formatter={(v) => this.formatCurrency(v)} />
                          <Bar dataKey="total" fill="#c62828" name="Gasto" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-muted text-center py-3 mb-0">Sin gastos registrados en el mes seleccionado.</p>
                    )}
                  </MDBCardBody>
                </MDBCard>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>

          <MDBCol xl="6">
            <MDBCard className="h-100">
              <MDBCardBody>
                <MDBCardTitle className="dashboard-section-title d-flex justify-content-between align-items-center">
                  <span>
                    <MDBIcon fas icon="chart-line" className="me-2" />
                    Resumen periodo seleccionado
                  </span>
                  <MDBBadge color="light" className="text-dark" pill>{periodLabel}</MDBBadge>
                </MDBCardTitle>

                {this.renderPeriodSelector()}

                <MDBRow className="g-3 mb-3">
                  <MDBCol md="12">
                    <MDBCard>
                      <MDBCardBody>
                        <MDBCardTitle className="dashboard-section-title">
                          <MDBIcon fas icon="wave-square" className="me-2" />
                          Evolución {this.state.periodUnit === "Y" ? "anual" : "mensual"} {viewLabel}
                        </MDBCardTitle>
                        {evolucionMensual && evolucionMensual.length > 0 ? (
                          <ResponsiveContainer width="100%" height={270}>
                            <LineChart data={evolucionMensual} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis dataKey="mes" tick={{ fontSize: 11 }} />
                              <YAxis tick={{ fontSize: 11 }} />
                              <Tooltip formatter={(v) => this.formatCurrency(v)} />
                              <Legend />
                              <Line type="monotone" dataKey="ingresos" stroke="#00796b" strokeWidth={2} dot={false} name="Ingresos" />
                              <Line type="monotone" dataKey="gastos" stroke="#c62828" strokeWidth={2} dot={false} name="Gastos" />
                            </LineChart>
                          </ResponsiveContainer>
                        ) : (
                          <p className="text-muted text-center py-3 mb-0">Sin datos en el periodo seleccionado.</p>
                        )}
                      </MDBCardBody>
                    </MDBCard>
                  </MDBCol>
                  <MDBCol md="12">
                    <ToggleablePieCard
                      title="Reparto de gasto del periodo"
                      data={gastoCategoriasPeriodo}
                      emptyText="Sin gastos para el periodo seleccionado."
                    />
                  </MDBCol>
                  <MDBCol md="12">
                    <CategoryBreakdownTabs
                      expensesTop={periodExpensesTop ?? []}
                      incomesTop={periodIncomesTop ?? []}
                      title="Top categorías del período"
                      emptyText="Sin datos en el período seleccionado."
                    />
                  </MDBCol>
                </MDBRow>
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>

        <MDBRow className="mt-2">
          <MDBCol md="12" className="mb-3">
            <MDBCard className="h-100">
              <MDBCardBody>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <MDBCardTitle className="dashboard-section-title mb-0">
                    <MDBIcon fas icon="sync-alt" className="me-2" />
                    Gastos periódicos{" "}
                    {ocurrenciasPendientes.length > 0 && (
                      <MDBBadge color="warning" pill className="ms-1">
                        {ocurrenciasPendientes.length} pendiente{ocurrenciasPendientes.length !== 1 ? "s" : ""}
                      </MDBBadge>
                    )}
                  </MDBCardTitle>
                  <MDBBtn
                    size="sm"
                    color="primary"
                    outline
                    onClick={() => this.setState({ showGastoPeriodicoModal: true, gastoToEdit: null })}
                  >
                    <MDBIcon fas icon="plus" className="me-1" />
                    Nuevo
                  </MDBBtn>
                </div>

                {loadingPeriodicos ? (
                  <div className="text-center py-2">
                    <MDBSpinner size="sm" grow color="primary" />
                  </div>
                ) : ocurrenciasPendientes.length === 0 ? (
                  <p className="text-muted text-center py-2 mb-0">No hay gastos periódicos pendientes de confirmar.</p>
                ) : (
                  <div className="dashboard-shared-list">
                    {ocurrenciasPendientes.map((o) => {
                      const idOcurrencia = o.IDOcurrencia || o.iDOcurrencia;
                      let periodoLabel = "";
                      try {
                        const d = new Date(o.periodo || o.Periodo);
                        periodoLabel = d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
                      } catch {}
                      const importe = o.importeEstimado || o.ImporteEstimado || 0;
                      return (
                        <div key={idOcurrencia} className="dashboard-shared-row align-items-center">
                          <div className="flex-grow-1">
                            <span className="dashboard-shared-name">{o.concepto || o.Concepto}</span>
                            <span className="text-muted small ms-2">({periodoLabel})</span>
                            <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                              {o.nombreCuenta || o.NombreCuenta} · {o.periodicidad || o.Periodicidad}
                            </div>
                          </div>
                          <MDBBadge color="secondary" pill className="me-2">
                            {this.formatCurrency(importe)}
                          </MDBBadge>
                          <MDBBtn
                            size="sm"
                            color="success"
                            outline
                            onClick={() =>
                              this.setState({
                                showConfirmarOcurrenciaModal: true,
                                ocurrenciaSeleccionada: o,
                              })
                            }
                          >
                            Confirmar
                          </MDBBtn>
                        </div>
                      );
                    })}
                  </div>
                )}

                {gastosPeriodicos.length > 0 && (
                  <div className="mt-3 border-top pt-2">
                    <div className="text-muted small fw-semibold mb-1">
                      Configurados ({gastosPeriodicos.length})
                    </div>
                    {gastosPeriodicos.map((gp) => {
                      const idGp = gp.IDGastoPeriodico || gp.iDGastoPeriodico;
                      return (
                        <div key={idGp} className="dashboard-shared-row align-items-center py-1">
                          <div className="flex-grow-1">
                            <span className="small">{gp.concepto || gp.Concepto}</span>
                            <span className="text-muted small ms-2">· {gp.periodicidad || gp.Periodicidad}</span>
                          </div>
                          <span className="text-muted small me-2">
                            {this.formatCurrency(gp.importeEstimado || gp.ImporteEstimado)}
                          </span>
                          <MDBBtn
                            size="sm"
                            color="secondary"
                            outline
                            className="me-1"
                            onClick={() =>
                              this.setState({ showGastoPeriodicoModal: true, gastoToEdit: gp })
                            }
                          >
                            <MDBIcon fas icon="edit" />
                          </MDBBtn>
                          <MDBBtn
                            size="sm"
                            color="warning"
                            outline
                            className="me-1"
                            onClick={() => this.finalizarGastoPeriodico(gp)}
                            title="Finalizar"
                          >
                            <MDBIcon fas icon="flag-checkered" />
                          </MDBBtn>
                          <MDBBtn
                            size="sm"
                            color="danger"
                            outline
                            onClick={() => this.deleteGastoPeriodico(gp)}
                            title="Eliminar"
                          >
                            <MDBIcon fas icon="trash" />
                          </MDBBtn>
                        </div>
                      );
                    })}
                  </div>
                )}
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>

        <MDBRow className="mt-2">
          <MDBCol md="12" className="mb-4">
            <MDBCard className="h-100">
              <MDBCardBody>
                <MDBCardTitle className="dashboard-section-title">
                  <MDBIcon fas icon="users" className="me-2" />
                  Cuentas de gastos compartidos
                </MDBCardTitle>
                {cuentasCompartidas && cuentasCompartidas.length > 0 ? (
                  <div className="dashboard-shared-list">
                    {cuentasCompartidas.map((cc) => (
                      <div key={cc.IDCuenta} className="dashboard-shared-row">
                        <span className="dashboard-shared-name">{cc.nombre}</span>
                        <MDBBadge color={cc.miBalance >= 0 ? "success" : "danger"} pill className="dashboard-shared-badge">
                          {this.formatCurrency(cc.miBalance)}
                        </MDBBadge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted text-center py-3">No tienes cuentas de gastos compartidos.</p>
                )}
              </MDBCardBody>
            </MDBCard>
          </MDBCol>
        </MDBRow>
      </MDBContainer>

      {/* Modal: Crear / Editar gasto periódico */}
      <GastoPeriodicoModal
        show={showGastoPeriodicoModal}
        gastoToEdit={gastoToEdit}
        cuentas={cuentasDisponibles}
        categorias={categorias}
        onClose={() => this.setState({ showGastoPeriodicoModal: false, gastoToEdit: null })}
        onSaved={this.onGastoSaved}
        onLoading={(v) => this.setState({ loadingPeriodicos: v })}
      />

      {/* Modal: Confirmar / Rechazar ocurrencia */}
      <ConfirmarOcurrenciaModal
        show={showConfirmarOcurrenciaModal}
        ocurrencia={ocurrenciaSeleccionada}
        onClose={() => this.setState({ showConfirmarOcurrenciaModal: false, ocurrenciaSeleccionada: null })}
        onConfirmada={this.onOcurrenciaConfirmada}
        onRechazada={this.onOcurrenciaRechazada}
        onLoading={(v) => this.setState({ loadingPeriodicos: v })}
      />
      </>
    );
  }
}

